import { defineStore } from 'pinia'
import navigationService from '@/services/navigation.service'
import oppositionsService from '@/services/oppositions.service'
import { navFor } from '@/utils/access'
import { useAuthStore } from './auth.store'

/**
 * Sidebar / topbar navigation tree.
 *
 * Kept as its own store so permission-based filtering lives in one getter
 * without touching Sidebar.vue — the client-viewer rule does (W11.M9).
 */
export interface NavigationState {
  tree: { workspace: any[]; tools: any[] }
  loading: boolean
  error: unknown
}

export const useNavigationStore = defineStore('navigation', {
  state: (): NavigationState => ({
    tree: { workspace: [], tools: [] },
    loading: false,
    error: null,
  }),
  getters: {
    /* Filtered by account tier: a client viewer is not shown the dashboard,
     * monitoring or reports, which the router would not let them open. */
    workspace: (state) => navFor(state.tree.workspace || [], useAuthStore().user.userKind),
    tools: (state) => navFor(state.tree.tools || [], useAuthStore().user.userKind),
  },
  actions: {
    async fetch() {
      if (this.tree.workspace.length) return // already loaded
      this.loading = true
      try {
        this.tree = await navigationService.get()
      } catch (err) {
        this.error = err
      } finally {
        this.loading = false
      }
      await this.refreshCaseCount()
    },

    /**
     * The Oppositions badge is the number of *active* cases the caller can
     * see, from `/procedures/summary` — the same number the list page's first
     * tile shows. It was a literal 7 in the fixture. A failed summary shows no
     * badge rather than a stale or invented one.
     */
    async refreshCaseCount() {
      try {
        const summary = await oppositionsService.summary()
        this.setCount('oppositions', summary.active)
      } catch {
        this.setCount('oppositions', null)
      }
    },

    setCount(key: string, count: number | null) {
      for (const item of [...(this.tree.workspace ?? []), ...(this.tree.tools ?? [])]) {
        if (item.key === key) {
          if (count == null) delete item.count
          else item.count = count
        }
      }
    },
  },
})
